import { trackingStore } from '@stores/Tracking';
import { useCallback, useEffect, useRef } from 'react';
import { SelectionSpan } from '../types';
import { anchorForGrab, isAdjustable, spanFrom } from './selectionSpan';

/**
 * How far the pointer must travel before the gesture is a range-mark rather than a seek.
 *
 * Small enough that a deliberate drag is never mistaken for a click, large enough that the
 * hand tremor in a click on a trackpad is. Below one CSS pixel of movement per frame at any
 * plausible drag speed, so it is crossed on the first move of a real drag.
 */
const DRAG_THRESHOLD_PX = 4;

/**
 * How close to a handle a press counts as grabbing it, in CSS pixels either side.
 *
 * Deliberately wider than the handle looks. The handle is a few pixels of white on an 8px bar,
 * and this is the only way to make it catchable without drawing something bigger — which would
 * start to cover the range it marks.
 *
 * Pixels, not a percentage of the bar: this is a question about aiming, and what the hand has
 * to do to hit a target does not change with how wide the bar happens to be. As a percentage it
 * did — 1.5% is 9px of a desktop bar but under 4px of a phone's, narrower than the handle drawn
 * there, so the handle was unhittable on exactly the device with no cursor to aim with.
 */
const HANDLE_GRAB_PX = 10;

/** Percent along the bar a client-X position sits at, given the bar's own rect. */
const percentIn = (rect: DOMRect, clientX: number) =>
  Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));

interface UseProgressBarParams {
  totalDuration: number;
  scrubTo: (time: number) => void;
  setHoverTime: (time: number) => void;
  setHoverPosition: (position: number) => void;
  setShowHoverTime: (show: boolean) => void;
  progressBarHitboxRef: React.RefObject<HTMLDivElement | null>;
  /** Present only where marking a range is offered — see `onSelectionChange` on the player. */
  onSelectionChange?: (selection: SelectionSpan) => void;
  /**
   * The range currently marked, so a press near one of its edges can grab that edge instead of
   * starting a new range. Read only for that decision — the band itself is drawn by the caller.
   */
  selection?: SelectionSpan;
  /**
   * A range drag started or ended — see `onSelectionDragChange` on the player for what the owner
   * does with it. Fires only for the gesture that marks a range, never for a seek.
   */
  onSelectionDragChange?: (dragging: boolean) => void;
}

/**
 * Everything the scrub bar does with a pointer, in one place.
 *
 * The bar has three gestures on one element and they have to be told apart by the same code,
 * because the distinction is "what did this press turn out to mean" and that is only known on
 * release: a press that goes nowhere is a seek, a press that travels marks a range, and a
 * press that starts on a handle moves that edge. Splitting them across `onClick` plus a drag
 * listener would mean a completed drag also fires the element's click — so the seek would have
 * to be suppressed by a flag, and a flag set in one handler and read in another is exactly the
 * bookkeeping this shape avoids.
 */
export const useProgressBar = ({
  totalDuration,
  scrubTo,
  setHoverTime,
  setHoverPosition,
  setShowHoverTime,
  progressBarHitboxRef,
  onSelectionChange,
  selection,
  onSelectionDragChange,
}: UseProgressBarParams) => {
  /**
   * The live gesture. A ref, not state: it is written on every pointer move and read by the
   * handlers only — nothing renders from it, and making it state would re-render the whole
   * control row at pointer-event rate for no visible change.
   *
   * `anchorPercent` is the edge that stays put: the press position when creating a range, the
   * opposite handle when dragging one. That is what lets both cases share one move handler.
   */
  const gestureRef = useRef<{
    pointerId: number;
    startX: number;
    anchorPercent: number;
    moved: boolean;
  } | null>(null);

  /**
   * Whether the live gesture has become a range drag, and has been reported as one.
   *
   * Separate from `gestureRef.moved` because it is a fact about the *outside*: it says an
   * `onSelectionDragChange(true)` is outstanding and owes a matching `false`, from whichever of
   * release, cancel and unmount gets there first.
   */
  const draggingRef = useRef(false);

  /**
   * Coalesces the range emitted while dragging down to one per frame.
   *
   * A pointer can report faster than the display refreshes — more so with a high-polling mouse,
   * and `pointermove` may already be a batch of coalesced events — and each emission here is a
   * store write that re-renders the modal above this player. The band cannot move more than
   * once a frame however often we emit, so the extra writes buy nothing. `MomentHead` throttles
   * its own drag the same way.
   */
  const pendingFrameRef = useRef<number | null>(null);

  // Through a ref so the unmount cleanup below can reach the current callback without listing
  // it as a dependency — a dependency there would tear the gesture down mid-drag every time the
  // owner re-rendered, which during a drag is every frame (see `stable-callbacks.md`).
  const onSelectionDragChangeRef = useRef(onSelectionDragChange);
  onSelectionDragChangeRef.current = onSelectionDragChange;

  /**
   * The one writer of the drag signal, so the three handlers that end a gesture cannot report
   * it twice or leave it latched on.
   */
  const setDragging = useCallback((dragging: boolean) => {
    if (draggingRef.current === dragging) return;
    draggingRef.current = dragging;
    onSelectionDragChangeRef.current?.(dragging);
  }, []);

  useEffect(
    () => () => {
      if (pendingFrameRef.current !== null) cancelAnimationFrame(pendingFrameRef.current);
      // Unmounting mid-drag is the one ending that sends no pointer event of its own, and the
      // flag it feeds is shared — see `onSelectionDragChange`.
      setDragging(false);
    },
    [setDragging],
  );

  const barRect = useCallback(() => {
    const rect = progressBarHitboxRef.current?.getBoundingClientRect();
    return rect && rect.width > 0 ? rect : null;
  }, [progressBarHitboxRef]);

  const percentAt = useCallback(
    (clientX: number) => {
      const rect = barRect();
      return rect ? percentIn(rect, clientX) : null;
    },
    [barRect],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const rect = barRect();
      if (!rect) return;
      const percent = percentIn(rect, e.clientX);

      // Capture even where selection is off, so a press that ends outside the bar still
      // resolves to the seek it was rather than being lost with the pointer.
      e.currentTarget.setPointerCapture(e.pointerId);

      // A press within a handle's reach grabs that edge and anchors the other one, so the
      // existing range is adjusted rather than replaced. A press anywhere else — including
      // inside the band — starts a new range, which is the same rule a text selection follows.
      //
      // The tolerance is converted here because this is the layer that has the bar's width;
      // `anchorForGrab` is given it already in the bar's own units.
      const grabbable = onSelectionChange && selection && isAdjustable(selection);
      const grabAnchor = grabbable
        ? anchorForGrab(percent, selection, (HANDLE_GRAB_PX / rect.width) * 100)
        : null;

      gestureRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        anchorPercent: grabAnchor ?? percent,
        moved: false,
      };
    },
    [barRect, onSelectionChange, selection],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // The hover readout runs whether or not a gesture is in flight — it is what tells the
      // user which second they are about to seek to, or drag to.
      const percent = percentAt(e.clientX);
      if (percent !== null && totalDuration) {
        setHoverTime((percent / 100) * totalDuration);
        setHoverPosition(percent);
        setShowHoverTime(true);
      }

      const gesture = gestureRef.current;
      if (!gesture || gesture.pointerId !== e.pointerId || !onSelectionChange) return;
      if (!gesture.moved && Math.abs(e.clientX - gesture.startX) < DRAG_THRESHOLD_PX) return;
      if (percent === null) return;

      gesture.moved = true;
      setDragging(true);
      const span = spanFrom(gesture.anchorPercent, percent);
      if (!span) return;

      if (pendingFrameRef.current !== null) cancelAnimationFrame(pendingFrameRef.current);
      pendingFrameRef.current = requestAnimationFrame(() => {
        pendingFrameRef.current = null;
        onSelectionChange(span);
      });
    },
    [
      percentAt,
      totalDuration,
      setHoverTime,
      setHoverPosition,
      setShowHoverTime,
      onSelectionChange,
      setDragging,
    ],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const gesture = gestureRef.current;
      gestureRef.current = null;
      setDragging(false);

      // A finger has no `mouseleave`, so nothing else would ever take the readout back down —
      // it would sit over the bar until the next tap. A mouse keeps it: the cursor is still
      // there, and hiding it on every click would make the timestamp flicker while scrubbing.
      if (e.pointerType !== 'mouse') setShowHoverTime(false);

      if (!gesture || gesture.pointerId !== e.pointerId) return;

      // A gesture that marked a range already committed it on the way — releasing just ends it.
      if (gesture.moved) return;

      const percent = percentAt(e.clientX);
      if (percent === null) return;
      const time = (percent / 100) * totalDuration;
      trackingStore.timelineSeeked({ timestamp: time });
      scrubTo(time);
    },
    [percentAt, totalDuration, scrubTo, setShowHoverTime, setDragging],
  );

  /**
   * A cancelled pointer (the browser taking it for a system gesture, the element unmounting
   * mid-drag) ends the gesture without committing anything further. Whatever the last move
   * reported stays — the range is committed live, so there is nothing half-written to undo.
   */
  const handlePointerCancel = useCallback(() => {
    gestureRef.current = null;
    setDragging(false);
    setShowHoverTime(false);
  }, [setShowHoverTime, setDragging]);

  const handleProgressLeave = useCallback(() => {
    setShowHoverTime(false);
  }, [setShowHoverTime]);

  const handleProgressEnter = useCallback(() => {
    setShowHoverTime(true);
  }, [setShowHoverTime]);

  return {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    handleProgressLeave,
    handleProgressEnter,
  };
};
